import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";
import FeedbackDetailModal from "../components/FeedbackDetailModal";
import BillDigestModal from "../components/BillDigestModal";
import "./notifications.css";

const PAGE_SIZE = 20;
const DIGEST_TYPES = ["bill_status_digest_personal", "bill_status_digest_workspace"];

const TYPE_ICON = {
    feedback_submitted: "bi-envelope-check-fill",
    feedback_halfway_pending: "bi-hourglass-split",
    task_due_soon: "bi-clock-fill",
    task_overdue: "bi-exclamation-triangle-fill",
    task_change_request: "bi-arrow-repeat",
    bill_amended: "bi-file-earmark-diff-fill",
    rulemaking_notice: "bi-journal-text",
    bill_status_digest_personal: "bi-person-lines-fill",
    bill_status_digest_workspace: "bi-people-fill",
};

const NOTIFICATION_TYPES = [
    {
        type: "feedback_submitted",
        label: "Feedback submitted",
        cadence: "Instant",
        when: "A client or stakeholder contact submits feedback on bill(s) you requested their input on.",
        includes: "Who responded, the request title, and — click to expand — their notes on each bill.",
        action: "Opens a modal showing that respondent's notes for each bill in the request.",
    },
    {
        type: "feedback_halfway_pending",
        label: "Feedback request pending",
        cadence: "Nightly check (~3am PT)",
        when: "A feedback request you created is halfway to its deadline and hasn't received any responses yet.",
        includes: "The request title, as a reminder to follow up with recipients.",
        action: "Takes you to the Teams page, where the feedback request lives.",
    },
    {
        type: "task_due_soon",
        label: "Task coming due",
        cadence: "Nightly check (~3am PT)",
        when: "A task assigned to you is due within the next 2 days and isn't marked complete.",
        includes: "The task description, the bill it's tied to, and the due date.",
        action: "Takes you to the bill's profile page, where the task is listed.",
    },
    {
        type: "task_overdue",
        label: "Task overdue",
        cadence: "Instant + nightly check",
        when: "A task assigned to you passes its due date without being marked complete. Fires right away if a task is created with a due date already in the past, and is checked nightly for anything that becomes overdue afterward.",
        includes: "The task description, the bill it's tied to, and the due date.",
        action: "Takes you to the bill's profile page, where the task is listed.",
    },
    {
        type: "task_change_request",
        label: "Task change requested",
        cadence: "Instant",
        when: "Someone you assigned a task to asks for a new due date or priority from the task's detail modal.",
        includes: "Who asked, the task, what they want changed, and their reason if they gave one.",
        action: "Takes you to the Workspace, where you can edit the task's due date or priority.",
    },
    {
        type: "bill_amended",
        label: "Tracked bill amended",
        cadence: "Nightly check (~3am PT)",
        when: "A High- or Critical-priority bill your team or client is tracking receives an amended version.",
        includes: "The bill number and title. Sent to the team admin, account managers, and anyone assigned as Lead on that bill.",
        action: "Takes you to the bill's profile page.",
    },
    {
        type: "rulemaking_notice",
        label: "Rulemaking notice for a tracked bill",
        cadence: "Weekly (Saturday ~3am PT, after the Friday Notice Register)",
        when: "A proposed regulation in the California Regulatory Notice Register cites a chaptered bill you or your team is tracking, by chapter or bill number.",
        includes: "The bill, the issuing agency, the regulation's subject, and its hearing date and comment deadline when listed. Sent to the team admin, account managers and Leads for team tracking, or to you for personal tracking.",
        action: "Takes you to the bill's profile page.",
    },
    {
        type: "bill_status_digest_personal",
        label: "Your tracked bills: status updates",
        cadence: "Nightly check (~3am PT)",
        when: "One or more bills you're personally tracking are vetoed, signed by the Governor, or chaptered.",
        includes: "A single digest grouping every affected bill by action type (Vetoed, Signed by Governor, Chaptered) — not a separate notification per bill.",
        action: "Opens a modal listing every bill in the digest, grouped by action type and linked to its bill profile page.",
    },
    {
        type: "bill_status_digest_workspace",
        label: "Workspace tracked bills: status updates",
        cadence: "Nightly check (~3am PT)",
        when: "One or more bills your Workspace or a client/stakeholder group is tracking are vetoed, signed by the Governor, or chaptered. Sent to the team admin, account managers, and anyone assigned as Lead (Leads only see bills they're assigned to).",
        includes: "A single digest per Workspace grouping every affected bill by action type (Vetoed, Signed by Governor, Chaptered) — not a separate notification per bill.",
        action: "Opens a modal listing every bill in the digest, grouped by action type and linked to its bill profile page.",
    },
];

export default function Notifications() {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [view, setView] = useState("active");
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState(null);
    const [billDigestModal, setBillDigestModal] = useState(null);

    const handleViewChange = (nextView) => {
        if (nextView === view) return;
        setNotifications([]);
        setView(nextView);
    };

    const fetchPage = useCallback((nextOffset, activeView) => {
        if (!auth.user || activeView === "about") return;
        setLoading(true);
        API.getNotifications({ user_id: auth.user.user_id, limit: PAGE_SIZE, offset: nextOffset, archived: activeView === "archived" })
            .then(res => {
                setNotifications(prev => nextOffset === 0 ? res.data : [...prev, ...res.data]);
                setHasMore(res.data.length === PAGE_SIZE);
                setOffset(nextOffset);
            })
            .catch(err => console.error("Error fetching notifications:", err))
            .finally(() => setLoading(false));
    }, [auth.user]);

    useEffect(() => {
        fetchPage(0, view);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.user, view]);

    const broadcastUnreadCount = () => {
        API.getNotificationUnreadCount(auth.user.user_id)
            .then(res => window.dispatchEvent(new CustomEvent('notifUnreadCount', { detail: { count: res.data.count } })))
            .catch(() => {});
    };

    const handleNotifClick = (notification) => {
        if (!notification.is_read) {
            API.markNotificationRead(notification.id, auth.user.user_id)
                .then(broadcastUnreadCount)
                .catch(() => {});
            setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n));
        }
        if (notification.type === "feedback_submitted" && notification.related_id) {
            const [requestId, contactId] = notification.related_id.split("-");
            setFeedbackModal({ requestId, contactId });
            return;
        }
        if (DIGEST_TYPES.includes(notification.type)) {
            setBillDigestModal({ notificationId: notification.id, title: notification.title });
            return;
        }
        if (notification.link) navigate(notification.link);
    };

    const handleMarkAllRead = () => {
        API.markAllNotificationsRead(auth.user.user_id)
            .then(() => {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                broadcastUnreadCount();
            })
            .catch(() => {});
    };

    const handleToggleRead = (e, notification) => {
        e.stopPropagation();
        const markUnread = notification.is_read;
        const request = markUnread
            ? API.markNotificationUnread(notification.id, auth.user.user_id)
            : API.markNotificationRead(notification.id, auth.user.user_id);
        request
            .then(() => {
                setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: !markUnread } : n));
                broadcastUnreadCount();
            })
            .catch(() => {});
    };

    const handleArchive = (e, notification) => {
        e.stopPropagation();
        API.archiveNotification(notification.id, auth.user.user_id)
            .then(() => {
                setNotifications(prev => prev.filter(n => n.id !== notification.id));
                broadcastUnreadCount();
            })
            .catch(() => {});
    };

    const handleUnarchive = (e, notification) => {
        e.stopPropagation();
        API.unarchiveNotification(notification.id, auth.user.user_id)
            .then(() => {
                setNotifications(prev => prev.filter(n => n.id !== notification.id));
                broadcastUnreadCount();
            })
            .catch(() => {});
    };

    const formatDate = (dateStr) => new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });

    const hasUnread = notifications.some(n => !n.is_read);

    return (
        <div className="notifications-page container">
            <div className="notifications-page-header">
                <h4 className="mb-0">Notifications</h4>
                {view === "active" && hasUnread && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleMarkAllRead}>Mark all as read</button>
                )}
            </div>

            <div className="notifications-page-tabs">
                <button
                    className={`notifications-page-tab ${view === "active" ? "notifications-page-tab--active" : ""}`}
                    onClick={() => handleViewChange("active")}
                >
                    Active
                </button>
                <button
                    className={`notifications-page-tab ${view === "archived" ? "notifications-page-tab--active" : ""}`}
                    onClick={() => handleViewChange("archived")}
                >
                    Archived
                </button>
                <button
                    className={`notifications-page-tab ${view === "about" ? "notifications-page-tab--active" : ""}`}
                    onClick={() => handleViewChange("about")}
                >
                    About Notifications
                </button>
            </div>

            {view === "about" ? (
                <ul className="list-unstyled notifications-about-list">
                    {NOTIFICATION_TYPES.map(t => (
                        <li key={t.type} className="notifications-about-item">
                            <span className="notifications-about-icon">
                                <i className={`bi ${TYPE_ICON[t.type]}`} />
                            </span>
                            <div className="notifications-about-body">
                                <div className="notifications-about-header">
                                    <span className="notifications-about-label">{t.label}</span>
                                    <span className="notifications-about-cadence">{t.cadence}</span>
                                </div>
                                <p className="notifications-about-when"><strong>When:</strong> {t.when}</p>
                                <p className="notifications-about-includes"><strong>Includes:</strong> {t.includes}</p>
                                <p className="notifications-about-action"><strong>Clicking it:</strong> {t.action}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : notifications.length === 0 && loading ? (
                <p className="text-muted mt-3">Loading…</p>
            ) : notifications.length === 0 ? (
                <p className="text-muted mt-3">{view === "archived" ? "No archived notifications." : "No notifications yet."}</p>
            ) : (
                <ul className="list-unstyled notifications-page-list">
                    {notifications.map(n => (
                        <li key={n.id} className={`notifications-page-item ${!n.is_read ? 'notifications-page-item--unread' : ''}`} onClick={() => handleNotifClick(n)}>
                            <span className="notifications-page-icon">
                                <i className={`bi ${TYPE_ICON[n.type] || 'bi-bell-fill'}`} />
                            </span>
                            <div className="notifications-page-body">
                                <div className="notifications-page-title">{n.title}</div>
                                <div className="notifications-page-message">{n.message}</div>
                                <div className="notifications-page-time">{formatDate(n.createdAt)}</div>
                            </div>
                            <div className="notifications-page-actions">
                                {view === "active" ? (
                                    <>
                                        <i
                                            className={`bi ${n.is_read ? 'bi-envelope' : 'bi-envelope-open'}`}
                                            title={n.is_read ? "Mark as unread" : "Mark as read"}
                                            onClick={(e) => handleToggleRead(e, n)}
                                        />
                                        <i
                                            className="bi bi-archive"
                                            title="Archive"
                                            onClick={(e) => handleArchive(e, n)}
                                        />
                                    </>
                                ) : (
                                    <i
                                        className="bi bi-arrow-counterclockwise"
                                        title="Unarchive"
                                        onClick={(e) => handleUnarchive(e, n)}
                                    />
                                )}
                            </div>
                            {!n.is_read && <span className="notifications-page-dot" />}
                        </li>
                    ))}
                </ul>
            )}

            {view !== "about" && hasMore && (
                <div className="text-center mt-3 mb-4">
                    <button className="btn btn-sm btn-outline-primary" disabled={loading} onClick={() => fetchPage(offset + PAGE_SIZE, view)}>
                        {loading ? 'Loading…' : 'Load More'}
                    </button>
                </div>
            )}

            {feedbackModal && (
                <FeedbackDetailModal
                    requestId={feedbackModal.requestId}
                    contactId={feedbackModal.contactId}
                    onClose={() => setFeedbackModal(null)}
                />
            )}
            {billDigestModal && (
                <BillDigestModal
                    notificationId={billDigestModal.notificationId}
                    userId={auth.user?.user_id}
                    title={billDigestModal.title}
                    onClose={() => setBillDigestModal(null)}
                />
            )}
        </div>
    );
}
